<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Article } from '../../types'
import { useNavigationStore } from '../../stores/navigationStore'
import { useVehicleStore } from '../../stores/vehicleStore'
import DebugPanel from './DebugPanel.vue'

const props = defineProps<{
  article: Article
}>()

const navStore = useNavigationStore()
const vehicleStore = useVehicleStore()
const showDebug = ref(false)

const fallbackImg = '/Themes/Cylmo/Content/images/default-image.png'

// Fitment badge — only shown when we actually have evaluation inputs
// (VIN or motor code). Unrestricted parts never show a badge.
const fitmentBadge = computed(() => {
  if (!vehicleStore.selectedVin && !vehicleStore.selectedMotorCode) return null
  const status = props.article.fitment
  if (!status || status === 'Unrestricted') return null

  const reasons = props.article.fitmentReasons
  switch (status) {
    case 'Matches':
      return {
        cls: 'part-card__fitment--matches',
        label: 'Passer din bil',
        tooltip: reasons?.matched?.join(' · ') || 'Passer din bil',
      }
    case 'Mismatch':
      return {
        cls: 'part-card__fitment--mismatch',
        label: 'Passer ikke',
        tooltip: reasons?.mismatched?.join(' · ') || 'Denne delen passer ikke din bil',
      }
    case 'Unknown':
      return {
        cls: 'part-card__fitment--unknown',
        label: 'Kan ikke verifiseres',
        tooltip: 'Mangler chassisnummer eller motorkode for å verifisere',
      }
    default:
      return null
  }
})

function addToCart(productId: number) {
  const url = `/addproducttocart/catalog/${productId}/1/1`
  if ((window as any).AjaxCart) {
    ;(window as any).AjaxCart.addproducttocart_catalog(url)
  } else {
    window.location.href = url
  }
}

function openDebug(e: Event) {
  e.preventDefault()
  e.stopPropagation()
  showDebug.value = true
}
</script>

<template>
  <div class="part-card" :class="{ 'part-card--position-mismatch': article.positionMismatch }">
    <div v-if="article.positionMismatch || fitmentBadge" class="part-card__badges">
      <div
        v-if="article.positionMismatch"
        class="part-card__position-warn"
        title="Denne delen kan være for motsatt posisjon (for/bak). Sjekk spesifikasjonene før kjøp."
      >
        ⚠ Posisjon ikke bekreftet
      </div>
      <div
        v-if="fitmentBadge"
        class="part-card__fitment"
        :class="fitmentBadge.cls"
        :title="fitmentBadge.tooltip"
      >
        {{ fitmentBadge.label }}
      </div>
    </div>
    <a v-if="article.productUrl" :href="article.productUrl" class="part-card__link">
      <div class="part-card__image">
        <img
          :src="article.imageUrl || fallbackImg"
          :alt="article.genericArticleDescription"
          loading="lazy"
          @error="($event.target as HTMLImageElement).src = fallbackImg"
        />
      </div>
      <div class="part-card__body">
        <p class="part-card__brand">{{ article.mfrName }}</p>
        <p class="part-card__title">{{ article.genericArticleDescription }}</p>
        <p class="part-card__sku">Art.nr: {{ article.articleNumber }}</p>
        <div v-if="article.mandatoryCriteria?.length" class="part-card__specs">
          <span v-for="(c, i) in article.mandatoryCriteria" :key="i" class="part-card__spec">
            {{ c.description }}: {{ c.value }}<template v-if="c.unit"> {{ c.unit }}</template>
          </span>
        </div>
      </div>
    </a>
    <div v-else class="part-card__body part-card__body--no-link">
      <div class="part-card__image">
        <img :src="article.imageUrl || fallbackImg" :alt="article.genericArticleDescription" loading="lazy" />
      </div>
      <p class="part-card__brand">{{ article.mfrName }}</p>
      <p class="part-card__title">{{ article.genericArticleDescription }}</p>
      <p class="part-card__sku">Art.nr: {{ article.articleNumber }}</p>
    </div>

    <div class="part-card__footer">
      <template v-if="article.nopProductId">
        <div class="part-card__meta">
          <span v-if="article.stockAvailability" :class="{ 'stock--in': article.stockAvailability === 'På lager', 'stock--order': article.stockAvailability === 'Bestillingsvare', 'stock--out': article.stockAvailability === 'Ikke på lager' }">
            {{ article.stockAvailability }}
          </span>
          <span class="part-card__price" v-if="article.price">{{ article.price }}</span>
        </div>
        <button
          class="part-card__cart-btn"
          @click.prevent.stop="addToCart(article.nopProductId!)"
        >
          Kjøp
        </button>
      </template>
      <div v-else class="part-card__contact">
        <a href="/contactus-2" class="part-card__contact-link">Behov for denne? Kontakt oss</a>
      </div>
    </div>

    <!-- Admin debug button (TecdocEditor only) -->
    <button
      v-if="navStore.canEditCategories"
      class="part-card__debug-btn"
      title="Vis rå TecDoc-data for denne delen"
      @click.prevent.stop="openDebug"
    >
      Debug
    </button>
    <DebugPanel
      v-if="showDebug"
      :sku="article.articleNumber"
      @close="showDebug = false"
    />
  </div>
</template>

<style scoped lang="scss">
.part-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  }

  &__link {
    text-decoration: none;
    color: inherit;
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  &__image {
    aspect-ratio: 1;
    overflow: hidden;
    background: #f9fafb;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  }

  &__body {
    padding: 0.75rem;
    flex: 1;

    &--no-link {
      display: flex;
      flex-direction: column;
    }
  }

  &__brand {
    font-size: 0.75rem;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.25rem;
  }

  &__title {
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
    line-height: 1.3;
  }

  &__sku {
    font-size: 0.8rem;
    color: #888;
    font-family: monospace;
  }

  &__specs {
    margin-top: 0.5rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  &__spec {
    font-size: 0.75rem;
    background: #f0f4f8;
    padding: 2px 6px;
    border-radius: 4px;
    color: #555;
  }

  &__footer {
    padding: 0.5rem 0.75rem;
    border-top: 1px solid #f3f4f6;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
  }

  &__meta {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__price {
    font-weight: 700;
    font-size: 1rem;
  }

  &__cart-btn {
    background: #f5a623;
    color: white;
    border: none;
    padding: 6px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    font-size: 0.85rem;
    white-space: nowrap;

    &:hover { background: #e09000; }
  }

  &__stock {
    font-size: 0.8rem;
  }
}

.stock--in {
  color: #16a34a;
}

.stock--order {
  color: #d97706;
}

.stock--out {
  color: #dc2626;
}

.part-card__contact {
  width: 100%;
  text-align: center;
}

.part-card__contact-link {
  color: #002868;
  font-size: 0.8rem;
  text-decoration: underline;

  &:hover { color: #001d4f; }
}

.part-card {
  position: relative;

  &--position-mismatch {
    outline: 2px solid #f59e0b;
    outline-offset: -2px;
  }

  &__badges {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    max-width: calc(100% - 12px);
    pointer-events: none;
  }

  &__position-warn {
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #f59e0b;
    font-size: 0.65rem;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 4px;
    letter-spacing: 0.3px;
    text-align: center;
    line-height: 1.2;
    pointer-events: auto;
  }

  &__fitment {
    font-size: 0.65rem;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 4px;
    letter-spacing: 0.3px;
    line-height: 1.2;
    text-align: center;
    pointer-events: auto;
    cursor: help;

    &--matches {
      background: #dcfce7;
      color: #166534;
      border: 1px solid #16a34a;
    }

    &--mismatch {
      background: #fee2e2;
      color: #991b1b;
      border: 1px solid #dc2626;
    }

    &--unknown {
      background: #f3f4f6;
      color: #4b5563;
      border: 1px solid #9ca3af;
    }
  }

  &__debug-btn {
    position: absolute;
    top: 6px;
    left: 6px;
    background: rgba(31, 41, 55, 0.85);
    color: white;
    border: none;
    font-size: 0.65rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 4px;
    cursor: pointer;
    letter-spacing: 0.5px;
    text-transform: uppercase;

    &:hover { background: #111827; }
  }
}
</style>
